import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../shared/components/Button';
import { Modal } from '../../../shared/components/Modal';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { useMerchants } from '../../merchants/hooks/useMerchants';
import { useInstagramConnections, useResetInstagramConnection } from '../hooks/useInstagram';
import { InstagramFetchHealth, InstagramStatusPill } from '../components/InstagramConnectionFacts';
import { formatInstagramDate } from '../format';
import { InstagramConnectionStatus, type InstagramConnectionSummary } from '../types';

/**
 * TCS-admin Instagram oversight (PE-2178 tier 2, backend PE-2177).
 *
 * The only tier that works end-to-end today: the list needs MERCHANT.READ and reset needs
 * MERCHANT.UPDATE, both of which COLLECTIVE_SUPERADMIN/ADMIN already hold. watchman selects
 * no merchant name on this projection, so names are joined in from /merchants.
 *
 * Reset force-disconnects a stuck connection — it is a support action for a merchant whose
 * token has expired or whose fetches are erroring, not a routine one.
 */
export const InstagramConnectionsPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canReset = hasPermission(Permission.MERCHANT_UPDATE);

  const { data: connections = [], isLoading, error } = useInstagramConnections();
  const { data: merchants = [] } = useMerchants();
  const resetConnection = useResetInstagramConnection();

  const [statusFilter, setStatusFilter] = useState<'' | InstagramConnectionStatus>('');
  const [resetting, setResetting] = useState<InstagramConnectionSummary | undefined>();

  const merchantNameById = useMemo(
    () => new Map(merchants.map((merchant) => [merchant.id, merchant.name])),
    [merchants],
  );

  const visible = useMemo(
    () => (statusFilter ? connections.filter((c) => c.status === statusFilter) : connections),
    [connections, statusFilter],
  );

  const needsAttention = connections.filter(
    (c) =>
      c.status === InstagramConnectionStatus.ERROR ||
      c.status === InstagramConnectionStatus.EXPIRED ||
      c.status === InstagramConnectionStatus.REVOKED ||
      c.lastFetchSucceeded === false,
  ).length;

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Instagram Connections</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every merchant that has started an Instagram connection. {needsAttention} need
            {needsAttention === 1 ? 's' : ''} attention.
          </p>
        </div>
        <label className="block text-[14px] font-[600] text-foreground">
          Status
          <select
            className="px-3 border border-[#D9D9D9] bg-muted text-foreground rounded-[10px] h-[30px] text-[14px] focus:outline-none mt-1 block"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as '' | InstagramConnectionStatus)}
          >
            <option value="">All</option>
            {Object.values(InstagramConnectionStatus).map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <p className="text-sm text-red-500">Could not load Instagram connections.</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!isLoading && !error && visible.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {connections.length === 0
            ? 'No merchant has connected Instagram yet.'
            : 'No connections match this filter.'}
        </p>
      )}

      <div className="space-y-3">
        {visible.map((connection) => (
          <div
            key={connection.merchantId}
            className="rounded-lg border border-border bg-card p-4 space-y-2"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-foreground">
                    {merchantNameById.get(connection.merchantId) ?? connection.merchantId}
                  </span>
                  <InstagramStatusPill status={connection.status} />
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      connection.feedEnabled
                        ? 'bg-green-100 text-green-800'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {connection.feedEnabled ? 'Section shown' : 'Section hidden'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {connection.instagramUsername ? `@${connection.instagramUsername} · ` : ''}
                  token expires {formatInstagramDate(connection.tokenExpiresAt)}
                </p>
                <p className="text-xs mt-1">
                  <span className="text-muted-foreground">Last refresh: </span>
                  <InstagramFetchHealth
                    lastFetchAt={connection.lastFetchAt}
                    lastFetchSucceeded={connection.lastFetchSucceeded}
                    lastFetchError={connection.lastFetchError}
                  />
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate(`/merchants/${connection.merchantId}`)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Open merchant
                </button>
                {canReset && (
                  <button
                    onClick={() => setResetting(connection)}
                    className="text-sm text-red-600 hover:underline"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={!!resetting} onClose={() => setResetting(undefined)} size="sm">
        {resetting && (
          <div className="p-6 space-y-4">
            <h2 className="text-[16px] font-semibold text-foreground">Reset this connection?</h2>
            <p className="text-[14px] text-foreground">
              {merchantNameById.get(resetting.merchantId) ?? resetting.merchantId}'s stored access
              token is cleared and the connection goes back to Not connected, so they can
              reconnect from scratch. Their storefront Instagram section disappears until they
              do.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" size="sm" onClick={() => setResetting(undefined)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={async () => {
                  await resetConnection.mutateAsync(resetting.merchantId);
                  setResetting(undefined);
                }}
                disabled={resetConnection.isPending}
                loading={resetConnection.isPending}
              >
                Reset
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
